import { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GoabPageBlock, GoabSpacer, GoabModal, GoabButtonGroup, GoabButton, GoabText } from "@abgov/react-components";
import { selectCurrentUser } from "../../Store/api/auth/authSelectors";
import ApplicationList from "../Application/ApplicationList";
import { useGetApplicationListQuery } from "../../Store/api/application/applicationApi";
import { useGetProgramEventListQuery } from "../../Store/api/apiSlice";
import { selectProgram } from "../../Store/api/program/programSelectors";
import { getAvailableProgramEvents } from "../../MPP-Common-SPA/utils/getAvailableProgramEvents";
import { usePortalContext } from "../../PortalRunner/hooks/usePortalContext";
import { useLogin } from "../../PortalRunner/hooks/useLogin";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import { USER_APPLICATION_SESSION_KEY } from "../../MPP-Common-SPA/utils/globalUtility";
import { resolveErrorModalConfig } from "../../PortalRunner/global/errorModalConfig";
import NotFound from "../ErrorPages/NotFound";

export const Applications = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const isLoggedIn = user?.isLoggedIn;
  const isProxyUser = user?.isProxyUser;

  const program = useSelector(selectProgram);
  const {
    data: applicationList,
    isLoading: isApplicationsLoading,
    isSuccess: isApplicationsLoaded,
    refetch: refetchApplications,
    isFetching: isApplicationsFetching,
    isUninitialized,
  } = useGetApplicationListQuery(undefined, {
    skip: !isLoggedIn || isProxyUser,
  });
  const { data: programEvents, isSuccess: isEventsLoaded } = useGetProgramEventListQuery(program?.id || '', {
    skip: !program?.id,
  });
  const { withdrawApplication, isEventsEnabled, errorModalState, setErrorModalState } = usePortalContext({});
  const { handleLogout } = useLogin();
  const { getResourceValue } = useSessionManagement();

  useEffect(() => {
    if (!isUninitialized && isLoggedIn && !isProxyUser) {
      refetchApplications();
    }
  }, [isUninitialized, isLoggedIn, isProxyUser, refetchApplications]);

  const handleWithdrawApplication = (applicationId: any) => {
    withdrawApplication(applicationId).then((success: boolean) => {
      if (success) {
        refetchApplications();
      }
    });
  };

  const handleForbiddenAction = () => {
    setErrorModalState("");
    sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
    navigate("/applications", { replace: true });
  };

  const errorModalConfig = resolveErrorModalConfig(errorModalState);
  const errorModalActions = errorModalConfig.actions({
    handleForbiddenAction,
    handleLogout,
    dismiss: () => setErrorModalState(""),
  });

  const availableEvents = useMemo(() => {
    if (
      !isApplicationsLoaded ||
      !isEventsLoaded ||
      isEventsEnabled === undefined
    )
      return;

    return (
      isEventsEnabled &&
      getAvailableProgramEvents(programEvents, applicationList)
    );
  }, [
    applicationList,
    programEvents,
    isApplicationsLoaded,
    isEventsLoaded,
    isEventsEnabled,
  ]) as [];

  if (!programEvents) {
    return null;
  }

  if (isProxyUser) {
    return <NotFound proxyUser={true} />;
  }

  // Need to update the jsx components to tsx at some point to resolve typing errors

  return (
    <GoabPageBlock width="704px" testId="overviewContent">
      {!!errorModalState && (
        <GoabModal
          calloutVariant="emergency"
          heading={errorModalConfig.heading}
          open={!!errorModalState}
          actions={
            <GoabButtonGroup alignment="end">
              {errorModalActions.map(({ label, type, onClick }: any) => (
                <GoabButton key={label} type={type} onClick={onClick}>
                  {label}
                </GoabButton>
              ))}
            </GoabButtonGroup>
          }
        >
          <GoabText>{(errorModalState as any)?.message}</GoabText>
        </GoabModal>
      )}
      <>
        <GoabText size="heading-xl" mb="none">
          {getResourceValue({ key: "RESOURCES@myApplications.title" })}
        </GoabText>
        <GoabSpacer vSpacing="m" />
        <GoabText size="body-l" mt="none" mb="none">
          {getResourceValue({ key: "RESOURCES@myApplications.subTitle" })}
        </GoabText>
        <GoabSpacer vSpacing="2xl" />
        <ApplicationList
          applicationList={applicationList ?? []}
          isLoading={isApplicationsLoading || isApplicationsFetching}
          programEvents={programEvents ?? []}
          handleWithdrawApplication={handleWithdrawApplication}
          getResourceValue={getResourceValue}
          program={program}
          user={user}
        />
      </>
    </GoabPageBlock>
  );
};
